import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { auth } from '../../../firebase';

const InvoiceDataModal = ({ isOpen, onClose, onSuccess, driverAccount }) => {
  const [formData, setFormData] = useState({
    name: '',
    tax_identifier: '',
    address: '',
    city: '',
    subdivision_code: '',
    country_code: 'RO'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Romanian counties mapping
  const romanianCounties = {
    'AB': 'Alba',
    'AR': 'Arad',
    'AG': 'Argeș',
    'BC': 'Bacău',
    'BH': 'Bihor',
    'BN': 'Bistrița-Năsăud',
    'BT': 'Botoșani',
    'BV': 'Brașov',
    'BR': 'Brăila',
    'B': 'București',
    'BZ': 'Buzău',
    'CS': 'Caraș-Severin',
    'CL': 'Călărași',
    'CJ': 'Cluj',
    'CT': 'Constanța',
    'CV': 'Covasna',
    'DB': 'Dâmbovița',
    'DJ': 'Dolj',
    'GL': 'Galați',
    'GR': 'Giurgiu',
    'GJ': 'Gorj',
    'HR': 'Harghita',
    'HD': 'Hunedoara',
    'IL': 'Ialomița',
    'IS': 'Iași',
    'IF': 'Ilfov',
    'MM': 'Maramureș',
    'MH': 'Mehedinți',
    'MS': 'Mureș',
    'NT': 'Neamț',
    'OT': 'Olt',
    'PH': 'Prahova',
    'SM': 'Satu Mare',
    'SJ': 'Sălaj',
    'SB': 'Sibiu',
    'SV': 'Suceava',
    'TR': 'Teleorman',
    'TM': 'Timiș',
    'TL': 'Tulcea',
    'VS': 'Vaslui',
    'VL': 'Vâlcea',
    'VN': 'Vrancea'
  };

  // Initialize form with existing data
  useEffect(() => {
    if (isOpen && driverAccount?.invoiceData) {
      const invoiceData = driverAccount.invoiceData;
      setFormData({
        name: invoiceData.name || '',
        tax_identifier: invoiceData.tax_identifier || '',
        address: invoiceData.address || '',
        city: invoiceData.city || '',
        subdivision_code: invoiceData.subdivision_code || '',
        country_code: invoiceData.country_code || 'RO'
      });
    }
  }, [isOpen, driverAccount]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const { name, tax_identifier, address, city, subdivision_code } = formData;

    if (!name.trim()) {
      setError('Numele complet este obligatoriu');
      return false;
    }

    // CNP validation only if provided
    if (tax_identifier.trim() && (tax_identifier.length !== 13 || !/^\d{13}$/.test(tax_identifier))) {
      setError('CNP-ul trebuie să conțină exact 13 cifre');
      return false;
    }

    if (!address.trim()) {
      setError('Adresa este obligatorie');
      return false;
    }

    if (!city.trim()) {
      setError('Orașul este obligatoriu');
      return false;
    }

    if (!subdivision_code) {
      setError('Județul este obligatoriu');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch('https://api.servicegest.ro/driver/update', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceData: formData }),
      });

      if (!response.ok) {
        throw new Error('Eroare la salvarea datelor de facturare');
      }

      // Success - call onSuccess to refresh driver account and continue
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error updating invoice data:', err);
      setError(err.message || 'Eroare la salvarea datelor de facturare');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-30 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 touch-none">
        <div
          className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border dark:border-slate-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white">
                    Date Facturare
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Completează datele pentru emiterea facturilor
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto flex-1">
            {/* Important Notice */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-6">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium mb-1">Date obligatorii pentru facturare</p>
                <p>Pentru a putea efectua plăți și a primi facturi, este necesar să completezi toate datele de facturare.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Nume complet *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="ex: Popescu Ion"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors"
                  required
                />
              </div>

              {/* CNP */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  CNP (Opțional)
                </label>
                <input
                  type="text"
                  name="tax_identifier"
                  value={formData.tax_identifier}
                  onChange={handleInputChange}
                  placeholder="1234567891234"
                  maxLength="13"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Codul Numeric Personal (13 cifre)
                </p>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Adresă *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="ex: Strada Florilor nr. 123, bl. A, sc. 1, ap. 45"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors"
                  required
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Oraș *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="ex: Cluj-Napoca"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors"
                  required
                />
              </div>

              {/* County */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Județ *
                </label>
                <select
                  name="subdivision_code"
                  value={formData.subdivision_code}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors"
                  required
                >
                  <option value="">Selectează județul</option>
                  {Object.entries(romanianCounties).map(([code, name]) => (
                    <option key={code} value={code}>
                      {name} ({code})
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Security Notice */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-800 dark:text-blue-200">
                  <p className="font-medium">Protecția datelor</p>
                  <p>Toate datele tale personale sunt protejate conform GDPR și sunt folosite exclusiv pentru emiterea facturilor.</p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-lg font-semibold text-white ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 shadow-lg'
                  }`}
              >
                {loading ? 'Se salvează...' : 'Salvează date facturare'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceDataModal;